import React from 'react';
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import type { OrgMemberResponse } from './types';

// ─── RoleBadges ──────────────────────────────────────────────────────────────

const ROLE_LABELS = [
  { flag: 'is_assignable_clinician' as const, label: 'Clinician' },
  { flag: 'is_case_manager' as const, label: 'Case Mgr' },
  { flag: 'is_approver' as const, label: 'Approver' },
];

const RoleBadges: React.FC<{
  assignments: OrgMemberResponse['clinic_assignments'];
}> = React.memo(function RoleBadges({ assignments }) {
  const memberAssignments = assignments ?? [];
  const activeRoles = ROLE_LABELS.filter(({ flag }) =>
    memberAssignments.some(a => a[flag])
  );

  if (activeRoles.length === 0) {
    return <span className='text-sm text-muted-foreground'>&mdash;</span>;
  }

  return (
    <div className='flex gap-1 flex-wrap'>
      {activeRoles.map(({ flag, label }) => (
        <Badge key={flag} variant='outline' className='text-xs'>
          {label}
        </Badge>
      ))}
    </div>
  );
});

// ─── MemberRow ───────────────────────────────────────────────────────────────

type MemberRowProps = {
  member: OrgMemberResponse;
  onClick: (member: OrgMemberResponse) => void;
};

const MemberRow: React.FC<MemberRowProps> = React.memo(function MemberRow({
  member,
  onClick,
}) {
  const assignmentCount = (member.clinic_assignments ?? []).length;

  return (
    <TableRow className='cursor-pointer' onClick={() => onClick(member)}>
      <TableCell className='font-medium'>
        {member.first_name} {member.last_name}
      </TableCell>

      <TableCell className='text-muted-foreground'>{member.email}</TableCell>

      <TableCell>
        {assignmentCount > 0 ? (
          <Badge variant='secondary'>
            {assignmentCount} clinic{assignmentCount !== 1 ? 's' : ''}
          </Badge>
        ) : (
          <span className='text-sm text-muted-foreground'>None</span>
        )}
      </TableCell>

      <TableCell>
        <RoleBadges assignments={member.clinic_assignments} />
      </TableCell>
    </TableRow>
  );
});

// ─── MembersTable ────────────────────────────────────────────────────────────

type MembersTableProps = {
  members: OrgMemberResponse[];
  isLoading: boolean;
  hasSearchQuery: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onEditMember: (member: OrgMemberResponse) => void;
};

export const MembersTable: React.FC<MembersTableProps> = React.memo(
  function MembersTable({
    members,
    isLoading,
    hasSearchQuery,
    searchQuery,
    onSearchChange,
    onRefresh,
    onEditMember,
  }) {
    return (
      <>
        {/* Header */}
        <div className='flex justify-between items-center'>
          <h1 className='text-2xl font-bold tracking-tight'>
            Clinician Manager
          </h1>
          <Button variant='outline' onClick={onRefresh} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <RefreshCw className='h-4 w-4' />
            )}
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className='flex flex-wrap gap-4 items-end'>
          <div className='flex-1 min-w-[200px] max-w-sm'>
            <label
              htmlFor='member-search'
              className='text-sm font-medium mb-1.5 block'
            >
              Search
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                id='member-search'
                placeholder='Search by name or email...'
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                className='pl-9'
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <span className='ml-3 text-muted-foreground'>
              Loading organization members...
            </span>
          </div>
        ) : members.length === 0 ? (
          <div className='text-center text-muted-foreground py-8'>
            <p>
              {hasSearchQuery
                ? 'No members match your search.'
                : 'No organization members found.'}
            </p>
          </div>
        ) : (
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Clinics</TableHead>
                  <TableHead>Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(member => (
                  <MemberRow
                    key={member.workos_user_id}
                    member={member}
                    onClick={onEditMember}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </>
    );
  }
);
